import clsx from "clsx";
import { Datepicker } from "flowbite-react";
import { useCallback, useEffect, useState } from "react";
import Banner from "../../../../externalComponents/tailwind/banner";
import { Button } from "../../../../externalComponents/tailwind/button";
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from "../../../../externalComponents/tailwind/fieldset";
import {
  Radio,
  RadioField,
  RadioGroup,
} from "../../../../externalComponents/tailwind/radio";
import SelectInput from "../../../../externalComponents/tailwind/select-input";
import { Text } from "../../../../externalComponents/tailwind/text";
import {
  generateDateTimeArray,
  getNextTimeSlot,
  isDateTimeAfterToday,
} from "../../../../functions/date-utils";
import { stepCampaignEnum } from "./send-campaign-step.enum";

const Scheduler = ({ className, expanded, form, setForm }) => {
  const updateScheduleDateTime = useCallback(
    (_scheduleDateTime) => {
      setForm((_form) => ({
        ..._form,
        scheduleDateTime: _scheduleDateTime,
      }));
    },
    [form.scheduleDateTime],
  );

  function _updateScheduleDateTime(day, time, handler) {
    const scheduleDateTime = new Date(
      day.getFullYear(),
      day.getMonth(),
      day.getDate(),
      parseInt(time.split("h")[0]),
      parseInt(time.split("h")[1]),
    );
    handler(scheduleDateTime);
  }

  const now = new Date();
  const sendingMode = {
    now: "now",
    schedule: "schedule",
  };
  const minDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const maxDate = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() + 90,
  );

  const [selectedDates, setSelectedDates] = useState(minDate);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const options = generateDateTimeArray();
  const [selected, setSelected] = useState(getNextTimeSlot(options));

  useEffect(() => {
    _updateScheduleDateTime(selectedDates, selected, updateScheduleDateTime);
  }, []);

  const handleSelectChange = useCallback(
    (value) => {
      setSelected(value);
      _updateScheduleDateTime(selectedDates, value, updateScheduleDateTime);
    },
    [selectedDates],
  );

  const handleSelectedDates = useCallback(
    (date) => {
      setSelectedDates(date);
      _updateScheduleDateTime(date, selected, updateScheduleDateTime);
    },
    [selected],
  );

  const handleCategoryChange = useCallback(
    (value) => {
      const step = stepCampaignEnum.pacing;
      setForm((_form) => ({
        ..._form,
        isSendingNow: value === sendingMode.now,
        stepToFill: _form.stepToFill < step ? step : _form.stepToFill,
        expanded: new Set(_form.expanded).add(step),
      }));
    },
    [form.isSendingNow],
  );

  const customTheme = {
    popup: {
      root: {
        inner:
          "inline-block rounded-customBorderRadius bg- p-4 border w-full mb-5 dark:bg-gray-800",
      },
    },
    views: {
      days: {
        items: {
          base: "grid w-full grid-cols-7",
          item: {
            base: "block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100 dark:text-white dark:hover:bg-gray-600",
            selected: "bg-yellow hover:bg-yellow",
            disabled: "text-gray-400",
          },
        },
      },
    },
  };

  return (
    <>
      <Field
        className={clsx(
          "border rounded-customBorderRadius p-3 mt-5",
          className,
        )}
      >
        <Text className="font-bold">3. Schedule sending</Text>
        {expanded && (
          <>
            <FieldGroup className="mt-5">
              <Fieldset>
                <RadioGroup
                  name="category"
                  defaultValue={null}
                  onChange={handleCategoryChange}
                >
                  <RadioField>
                    <Radio value={sendingMode.now} />
                    <Label>Now</Label>
                  </RadioField>
                  <RadioField>
                    <Radio value={sendingMode.schedule} />
                    <Label>
                      <span>Schedule to: </span>
                      {form.isSendingNow === false ? (
                        <>
                          {form.scheduleDateTime.toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                          })}
                          {` [${timezone}]`}
                        </>
                      ) : (
                        <Button
                          color="secondaryButton"
                          className="ml-2 h-[32px]"
                          onClick={() => {
                            setForm((_form) => ({
                              ..._form,
                              isSendingNow: false,
                            }));
                          }}
                        >
                          Choose date and time
                        </Button>
                      )}
                    </Label>
                  </RadioField>
                </RadioGroup>
              </Fieldset>
            </FieldGroup>

            {form.isSendingNow === false && (
              <>
                <div className="w-[335px]">
                  {!isDateTimeAfterToday(form.scheduleDateTime) && (
                    <Banner
                      title="It's already past send-off time"
                      content="Please select a future slot."
                    />
                  )}
                  <Field>
                    <div className="mt-5">
                      <SelectInput
                        options={options}
                        selected={selected}
                        onChange={(value) => setSelected(value)}
                        onSelect={handleSelectChange}
                        value={selected}
                      />
                    </div>
                  </Field>
                  <Datepicker
                    theme={customTheme}
                    inline
                    minDate={minDate}
                    maxDate={maxDate}
                    onSelectedDateChanged={handleSelectedDates}
                    weekStart={1}
                    showClearButton={false}
                    showTodayButton={false}
                  />
                </div>
              </>
            )}
          </>
        )}
      </Field>
    </>
  );
};
export default Scheduler;
